import type { PropsWithChildren } from "react";

import { styled } from "@linaria/react";

export type SpacingSize = string | number;

export interface SpacingProps {
  margin?: SpacingSize | SpacingSize[];
  top?: SpacingSize;
  right?: SpacingSize;
  bottom?: SpacingSize;
  left?: SpacingSize;
  fillWidth?: boolean;
  fillHeight?: boolean;
}

function getSpacingSizeValue(value?: SpacingSize): string | undefined {
  if (typeof value === "number") {
    return `${value}px`;
  }
  return value;
}

function getSpacingSizeValueFromShorthand(
  margin?: SpacingSize | SpacingSize[],
): string | undefined {
  if (Array.isArray(margin)) {
    const cssValues = margin.map((val) => getSpacingSizeValue(val));
    return cssValues.join(" ");
  }
  return getSpacingSizeValue(margin);
}

const StyledSpacing = styled.div<{
  $margin?: string;
  $marginTop?: string;
  $marginRight?: string;
  $marginBottom?: string;
  $marginLeft?: string;
  $fillWidth?: boolean;
  $fillHeight?: boolean;
}>`
  margin: ${({ $margin }) => $margin || "unset"};
  margin-top: ${({ $marginTop }) => $marginTop || "unset"};
  margin-right: ${({ $marginRight }) => $marginRight || "unset"};
  margin-bottom: ${({ $marginBottom }) => $marginBottom || "unset"};
  margin-left: ${({ $marginLeft }) => $marginLeft || "unset"};
  width: ${({ $fillWidth }) => ($fillWidth ? "100%" : "auto")};
  height: ${({ $fillHeight }) => ($fillHeight ? "100%" : "auto")};
`;

const Spacing = ({
  children,
  margin,
  top,
  right,
  bottom,
  left,
  fillWidth,
  fillHeight,
}: PropsWithChildren<SpacingProps>) => {
  if (margin !== undefined) {
    return (
      <StyledSpacing
        $margin={getSpacingSizeValueFromShorthand(margin)}
        $fillWidth={fillWidth}
        $fillHeight={fillHeight}
      >
        {children}
      </StyledSpacing>
    );
  }

  return (
    <StyledSpacing
      $marginTop={getSpacingSizeValue(top)}
      $marginRight={getSpacingSizeValue(right)}
      $marginBottom={getSpacingSizeValue(bottom)}
      $marginLeft={getSpacingSizeValue(left)}
      $fillWidth={fillWidth}
      $fillHeight={fillHeight}
    >
      {children}
    </StyledSpacing>
  );
};

export default Spacing;
